import * as React from 'react';
import type { ReactNode } from 'react';
import { Button } from '../Button/Button';
import { buildTabId, buildTabPanelId } from '../Tabs/tabUtils';
import { scrollToElement } from '~/Features/Partials/Utils/utils';

export type TabKey = React.Key;

interface TabProps {
    children: ReactNode;
    tabKey: TabKey;
    selected: boolean;
    onSelect: (tabKey: TabKey) => void;
    className?: string;
}

export const Tab: React.FC<React.PropsWithChildren<TabProps>> = props => {
    const { children, tabKey, selected, onSelect, className } = props;

    const buttonRef = React.useRef<HTMLButtonElement>(null);
    const hasUserInteractedRef = React.useRef(false);

    const id = buildTabId(tabKey);
    const panelId = buildTabPanelId(tabKey);

    React.useEffect(() => {
        if (!hasUserInteractedRef.current) {
            return;
        }

        hasUserInteractedRef.current = false;

        if (selected && buttonRef.current) {
            scrollToElement(buttonRef.current, 'smooth', 'nearest', 'center');
        }
    }, [selected]);

    const onClick = () => {
        hasUserInteractedRef.current = true;
        onSelect(tabKey);
    };

    return (
        <Button
            id={id}
            ref={buttonRef}
            onClick={onClick}
            key={id}
            role="tab"
            aria-selected={selected}
            aria-controls={panelId}
            className={className}
        >
            {children}
        </Button>
    );
};
